import ButtonChat from "@/components/shared/futureChat/buttons/ButtonChat";
import { ImageProcessingContext } from "@/components/shared/imageProcessing/ImageProcessing";
import { TrackingContext } from "@/components/shared/tracking/Tracking";
import CardError from "@/components/web/cards/CardError";
import Spinner from "@/components/web/icons/Spinner";
import { cn } from "@/lib/utils";
import { inject, observer } from "mobx-react";
import ExportedImage from "next-image-export-optimizer";
import { useContext, useEffect, useState } from "react";
import { Store } from "stores/store";
import { getAltTextFromGeneralImage, getImageUrlFromGeneralImage, getStrapiAssetUrl } from "utils/imageHelpers";

type LoadingProps = {
    store?: Store;
    onReady: () => void;
    over50: boolean;
    onGoBack: () => void;
    onReadyOver50: () => void;
    onSelfie: () => void;
    selfieOrCall: "call" | "selfie"
    className?: string;
};

const Loading: React.FC<LoadingProps> = ({ store, onReady, over50, onReadyOver50, onGoBack, selfieOrCall, onSelfie, className }) => {
    if (!store) {
        throw Error("Component was not setup correctly");
    }

    const { screenView } = useContext(TrackingContext);

    const {
        loadingLocalized,
        globalLocalized,
        imageAgedUrl,
        imageProcessingState,
        cancelCallLocalized,
        chatbotLocalized,
        updateImageProcessingState,
        globalEventLocalized,
        imagePresentUrl,
        isLoading
    } = store;

    useEffect(() => {
        if (over50) {
            const timer = setTimeout(() => {
                console.log("IAM OVER 50");
                onReadyOver50();
            }, 2500);
            return () => clearTimeout(timer);
        }
        if (
            imageAgedUrl &&
            (imageProcessingState.positionInQueue === 0 || imageProcessingState.positionInQueue === undefined) &&
            !imageProcessingState.noFaceFound &&
            imagePresentUrl
        ) {
            console.log("Setting to ready");
            if (selfieOrCall === "call") {
                onReady();
            } else {
                onSelfie();
            }

        }
        // const delay = setTimeout(() => {}, 3000);
        // return () => clearTimeout(delay);
    }, [
        imageAgedUrl,
        imagePresentUrl,
        over50,
        imageProcessingState.noConnection,
        imageProcessingState.noFaceFound,
        imageProcessingState.positionInQueue,
        imageProcessingState.processingAborted,
    ]);

    //Seperate Variables and Conditions so we can display different error texts later on
    const [noConnection, setNoConnection] = useState(false);
    const [processingAborted, setProcessingAborted] = useState(false);
    const [noFaceFound, setNoFaceFound] = useState(false);
    const [positionInQueue, setPositionInQueue] = useState(0);

    useEffect(() => {
        if (noConnection !== imageProcessingState.noConnection) {
            setNoConnection(imageProcessingState.noConnection);
            screenView("3.3 - Connection Failed");
        }

        if (processingAborted !== imageProcessingState.processingAborted) {
            setProcessingAborted(imageProcessingState.processingAborted);
            screenView("3.4 - Connection Cancelled");
        }

        if (noFaceFound !== imageProcessingState.noFaceFound) {
            setNoFaceFound(imageProcessingState.noFaceFound);
            screenView("1.6 - Face Not Recognized");
        }

        if (imageProcessingState.positionInQueue !== undefined && imageProcessingState.positionInQueue > 0) {
            setPositionInQueue(imageProcessingState.positionInQueue);
            screenView("3.2 - Queue");
        } else if (imageProcessingState.positionInQueue === undefined) {
            setPositionInQueue(0);
        }

        if (!noConnection && !processingAborted && !noFaceFound && positionInQueue === 0) {
            setTimeout(() => {
                console.log("TIME IS UP, FACE NOT FOUND");
                updateImageProcessingState({
                    ...store.imageProcessingState,
                    noFaceFound: true,
                });
            }, 120000);
        }
    }, [
        imageProcessingState.noConnection,
        imageProcessingState.processingAborted,
        imageProcessingState.noFaceFound,
        imageProcessingState.positionInQueue,
    ]);

    useEffect(() => {
        console.log("User Position in Queue: ", imageProcessingState.positionInQueue);
    }, [imageProcessingState.positionInQueue]);

    const [currentIndex, setCurrentIndex] = useState(0);

    const [showSpinner, setShowSpinner] = useState(true);
    const [showCheck, setShowCheck] = useState(false);

    useEffect(() => {
        if (loadingLocalized?.loadingTexts) {
            const changeDisplay = () => {
                setShowSpinner(true);
                setShowCheck(false);

                const timeoutSpinner = setTimeout(() => {
                    setShowSpinner(false);
                    setShowCheck(true);
                }, 4000); // Zeige den Spinner für 4 Sekunden

                const timeoutText = setInterval(() => {
                    setCurrentIndex((prevIndex) => {
                        const nextIndex = prevIndex + 1;
                        return nextIndex < loadingLocalized.loadingTexts.length ? nextIndex : 0;
                    });
                }, 5000);

                return () => {
                    clearTimeout(timeoutSpinner);
                    clearTimeout(timeoutText);
                };
            };

            const interval = setInterval(changeDisplay, 5000);

            changeDisplay();

            screenView("3.1 - Transition");

            return () => clearInterval(interval);
        }
    }, [loadingLocalized]);

    const { cancelProcessing } = useContext(ImageProcessingContext);

    const handlePlayVideo = (e: { currentTarget: HTMLVideoElement }) => {
        if (e.currentTarget.readyState >= 0) {
            e.currentTarget.play().catch((err) => console.error(err)); // this returns a promise and thus is awaitable if need be
        }
    };

    return (
        <div className={cn("scrollbar-hidden absolute top-0 h-full w-full", className)}>
            {loadingLocalized?.background !== undefined && loadingLocalized?.background !== null && (
                <video
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "fill",
                        position: "absolute",
                    }}
                    //autoPlay https://stackoverflow.com/questions/76952112/web-ios-low-power-mode-video-playback
                    muted
                    loop
                    playsInline
                    onLoadedMetadata={handlePlayVideo}
                    onSuspend={handlePlayVideo}
                >
                    {loadingLocalized?.background.videoHD?.data?.attributes?.url !== undefined && (
                        <source
                            src={getStrapiAssetUrl(loadingLocalized?.background.videoHD?.data?.attributes?.url)}
                            type="video/mp4"
                        />
                    )}
                </video>
            )}
            <div className="flex w-full justify-center">
                {globalLocalized?.logo !== undefined && globalLocalized?.logo !== null && (
                    <div className="absolute top-[3px] z-[100] h-[80px] w-[125px]">
                        <ExportedImage
                            src={getImageUrlFromGeneralImage(globalLocalized.logo)}
                            alt={getAltTextFromGeneralImage(globalLocalized.logo)}
                            layout="fill"
                            objectFit="cover"
                            objectPosition="center"
                        />
                    </div>
                )}
                <div
                    className={cn(
                        "absolute flex h-full w-full items-center justify-center px-[16px]",
                        "2xs:w-[364px] 2xs:px-0",
                    )}
                >
                    {(noConnection || processingAborted || noFaceFound) && (
                        <CardError
                            icon={
                                <ExportedImage
                                    src={
                                        cancelCallLocalized?.buttonOne.icon !== undefined &&
                                            cancelCallLocalized?.buttonOne.icon !== null
                                            ? getImageUrlFromGeneralImage(cancelCallLocalized.buttonOne.icon)
                                            : ""
                                    }
                                    alt={
                                        cancelCallLocalized?.buttonOne.icon !== undefined &&
                                            cancelCallLocalized?.buttonOne.icon !== null
                                            ? getAltTextFromGeneralImage(cancelCallLocalized.buttonOne.icon)
                                            : ""
                                    }
                                    layout="fill"
                                    objectFit="cover"
                                    objectPosition="center"
                                />
                            }
                        >
                            {chatbotLocalized?.processingAbortedText}
                        </CardError>
                    )}
                    {!noConnection &&
                        !processingAborted &&
                        !noFaceFound &&
                        (positionInQueue === 0 || positionInQueue === undefined) && (
                            <div
                                className={cn(
                                    "flex w-full items-center justify-center rounded-[9.5px] bg-whiteShade-20 p-[16px] shadow-glass-effect backdrop-blur-[320px]",
                                    "font-frutigerBold text-[16px] tracking-minus-2 text-white text-shadow-card-shared-text-stele",
                                )}
                            >
                                {loadingLocalized?.loadingTexts !== undefined && (
                                    <div key={currentIndex} className="flex items-center gap-[7px]">
                                        {/* {loadingLocalized.checkAfterSpinner && <div className="relative h-[16px] max-h-[16px] min-h-[16px] w-[16px] min-w-[16px] max-w-[16px]">
                                            <ExportedImage
                                                src={`data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='90' height='90' fill='none' xmlns:v='https://vecta.io/nano'%3e%3cpath d='M31.609 54.458L17.111 39.96c-.781-.781-1.841-1.22-2.946-1.22s-2.164.439-2.945 1.22S10 41.801 10 42.906c0 .547.108 1.089.317 1.594s.516.965.903 1.351l17.464 17.465a4.16 4.16 0 0 0 5.891 0L78.78 19.111c.781-.781 1.22-1.841 1.22-2.946s-.439-2.164-1.22-2.946S76.939 12 75.834 12s-2.164.439-2.946 1.22l-41.28 41.238z' fill='%23fff'/%3e%3c/svg%3e`}
                                                alt={getAltTextFromGeneralImage(loadingLocalized?.checkAfterSpinner)}
                                                layout="fill"
                                                objectFit="cover"
                                                objectPosition="center"
                                            />
                                        </div>} */}

                                        {showCheck &&
                                            loadingLocalized?.checkAfterSpinner !== undefined &&
                                            loadingLocalized?.checkAfterSpinner !== null && (
                                                <div className="relative h-[20px] w-[20px]">
                                                    <ExportedImage
                                                        src={getImageUrlFromGeneralImage(
                                                            loadingLocalized?.checkAfterSpinner,
                                                        )}
                                                        alt={getAltTextFromGeneralImage(
                                                            loadingLocalized?.checkAfterSpinner,
                                                        )}
                                                        layout="fill"
                                                        objectFit="cover"
                                                        objectPosition="center"
                                                    />
                                                </div>
                                            )}

                                        {showSpinner && <Spinner />}

                                        {loadingLocalized.loadingTexts[currentIndex]?.title}
                                    </div>
                                )}
                            </div>
                        )}
                    {positionInQueue > 0 && !noConnection && !processingAborted && !noFaceFound && (
                        <div
                            className={cn(
                                "flex w-full flex-col items-center justify-center gap-[16px] rounded-[9.5px] bg-whiteShade-20 p-[20px] shadow-glass-effect backdrop-blur-[320px]",
                                "font-frutigerBold text-[10px] tracking-minus-2 text-white text-shadow-card-shared-text-stele",
                                "2xs:p-[24px] 2xs:text-[12px]",
                            )}
                        >
                            {chatbotLocalized?.loadingQueueText}
                            <div className="relative">
                                <Spinner />
                                <div className="absolute top-[20%] flex w-full items-center justify-center text-[24px] 2xs:text-[30px]">
                                    {positionInQueue}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div
                    className={cn(
                        "absolute bottom-[120px] w-full px-[16px] 2xs:bottom-[150px]",
                        "flex flex-col items-center gap-1 text-center",
                        "2xs:w-[342px] 2xs:px-0",
                        "font-frutigerBold text-[10px]",
                    )}
                >
                    <svg width="20" height="20" viewBox="0 0 90 90" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M45 40.7778V61.8889M45 83C40.0098 83 35.0684 82.0171 30.458 80.1074C25.8477 78.1977 21.6586 75.3987 18.1299 71.8701C14.6013 68.3414 11.8023 64.1523 9.89258 59.542C7.9829 54.9316 7 49.9902 7 45C7 40.0098 7.9829 35.0684 9.89258 30.458C11.8023 25.8477 14.6013 21.6586 18.1299 18.1299C21.6586 14.6013 25.8477 11.8023 30.458 9.89258C35.0684 7.9829 40.0098 7 45 7C55.0782 7 64.7437 11.0036 71.8701 18.1299C78.9964 25.2563 83 34.9218 83 45C83 55.0782 78.9964 64.7437 71.8701 71.8701C64.7437 78.9964 55.0782 83 45 83ZM45.2111 28.1111V28.5333H44.7889V28.1111H45.2111Z"
                            stroke="white"
                            strokeWidth="7"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    {selfieOrCall === "call" ? globalEventLocalized?.loadingInfoCall : globalEventLocalized?.loadingInfoSelfie}
                </div>
                <div className={cn("absolute bottom-[70px] w-full px-[16px]", "2xs:w-[364px] 2xs:px-0")}>
                    <ButtonChat
                        variant="secondary"
                        iconRight={
                            <ExportedImage
                                src={
                                    chatbotLocalized?.restartButtonLoading.icon !== undefined &&
                                        chatbotLocalized?.restartButtonLoading.icon !== null
                                        ? getImageUrlFromGeneralImage(chatbotLocalized?.restartButtonLoading.icon)
                                        : ""
                                }
                                alt={
                                    chatbotLocalized?.restartButtonLoading.icon !== undefined &&
                                        chatbotLocalized?.restartButtonLoading.icon !== null
                                        ? getAltTextFromGeneralImage(chatbotLocalized?.restartButtonLoading.icon)
                                        : ""
                                }
                                layout="fill"
                                objectFit="cover"
                                objectPosition="center"
                            />
                        }
                        onClick={() => {
                            cancelProcessing();
                            onGoBack();
                        }}
                    >
                        {chatbotLocalized?.restartButtonLoading.text}
                    </ButtonChat>
                </div>
            </div>
        </div>
    );
};

export default inject("store")(observer(Loading));
